import { ChronoUnit, LocalDate } from "@js-joda/core";
import { getStockholmLocalDateNow } from "./timeAndDate";

const MAX_DIGITS = 12;
const PERSONAL_IDENTITY_NUMBER_LENGTHS = /^(\d{10}|\d{12})$/;

const toDigits = (raw: string): string => (raw ?? "").replace(/\D/g, "");

/**
 * Expands a 2-digit year to the most recent past four-digit year: the current
 * century unless that lands in the future, in which case the previous century
 * (e.g. with "today" in 2026, `95` -> 1995, `20` -> 2020). This is enough to
 * validate the birth date — including whether Feb 29 was actually a leap day.
 */
const inferFullYear = (
  twoDigitYear: number,
  today: LocalDate = getStockholmLocalDateNow(),
): number => {
  const currentYear = today.year();
  const candidate = Math.floor(currentYear / 100) * 100 + twoDigitYear;
  return candidate > currentYear ? candidate - 100 : candidate;
};

/**
 * True when the leading birth date of the digits is a real calendar date.
 * The 12-digit form carries the full `YYYYMMDD`; for the 10-digit form the
 * 2-digit year is expanded via {@link inferFullYear} so leap days are checked
 * against the actual inferred year.
 */
const hasValidBirthDate = (digits: string): boolean => {
  const hasFullYear = digits.length === 12;
  const year = hasFullYear
    ? Number(digits.slice(0, 4))
    : inferFullYear(Number(digits.slice(0, 2)));
  const monthStart = hasFullYear ? 4 : 2;
  const month = Number(digits.slice(monthStart, monthStart + 2));
  const day = Number(digits.slice(monthStart + 2, monthStart + 4));

  try {
    LocalDate.of(year, month, day);
    return true;
  } catch {
    return false;
  }
};

/**
 * Normalizes a typed Swedish personal identity number to its canonical 12-digit
 * form (`YYYYMMDDXXXX`), digits only. Accepts input with or without a dash; the
 * 10-digit (`YYMMDD-XXXX`) form is expanded to 12 digits by inferring the
 * century via {@link inferFullYear}.
 */
export const normalizePersonalIdentityNumber = (raw: string): string => {
  const digits = toDigits(raw).slice(0, MAX_DIGITS);
  if (digits.length !== 10) return digits;

  const century = Math.floor(inferFullYear(Number(digits.slice(0, 2))) / 100);
  return `${century}${digits}`;
};

/**
 * True when the input's digits form exactly a 10-digit (`YYMMDD-XXXX`) or
 * 12-digit (`YYYYMMDD-XXXX`) personal identity number whose birth date is a real
 * calendar date. Validates the un-capped digits so any other count (including
 * more than 12) is rejected rather than silently truncated.
 */
export const isValidPersonalIdentityNumber = (raw: string): boolean => {
  const digits = toDigits(raw);
  return (
    PERSONAL_IDENTITY_NUMBER_LENGTHS.test(digits) && hasValidBirthDate(digits)
  );
};

/**
 * Formats a personal identity number for display.
 * Accepts `YYYYMMDDXXXX` and `YYYYMMDD-XXXX`; returns "-" when empty/invalid.
 */
export const formatPersonalIdentityNumber = (
  personalIdentityNumber: string | null,
): string => {
  const normalizedValue = (personalIdentityNumber ?? "").trim();
  if (normalizedValue.length === 0) return "-";
  if (/^\d{8}-\d{4}$/.test(normalizedValue)) return normalizedValue;
  if (/^\d{12}$/.test(normalizedValue)) {
    return `${normalizedValue.slice(0, 8)}-${normalizedValue.slice(8, 12)}`;
  }
  return "-";
};

/**
 * Derives the worker's age in whole years from a Swedish personal identity number.
 * Reads the birth date from the leading `YYYYMMDD`; returns null when empty/invalid.
 */
export const getAgeFromPersonalIdentityNumber = (
  personalIdentityNumber: string | null,
  today: LocalDate = getStockholmLocalDateNow(),
): number | null => {
  const digits = (personalIdentityNumber ?? "").replace(/\D/g, "");
  if (digits.length < 8) return null;

  const year = Number(digits.slice(0, 4));
  const month = Number(digits.slice(4, 6));
  const day = Number(digits.slice(6, 8));

  let birthDate: LocalDate;
  try {
    birthDate = LocalDate.of(year, month, day);
  } catch {
    return null;
  }

  if (birthDate.isAfter(today)) return null;
  return birthDate.until(today, ChronoUnit.YEARS);
};
